"use client";

import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from "react";
import { useResponsiveLayout, BREAKPOINTS } from "@/hooks/useResponsiveLayout";

const PreviewModeContext = createContext();

export const PREVIEW_WIDTHS = {
  desktop: "max-w-7xl", // Default max width
  tablet: "max-w-3xl", // ~768px
  mobile: "max-w-md", // ~480px
};

export function PreviewModeProvider({ children }) {
  const [previewMode, setPreviewMode] = useState(null);
  const { windowWidth } = useResponsiveLayout();

  // Update preview mode and apply appropriate classes to body
  const updatePreviewMode = useCallback((mode) => {
    setPreviewMode(mode);

    // Add appropriate class to body for CSS targeting
    if (mode === "mobile") {
      document.body.classList.add("mobile-preview");
      document.body.classList.remove("tablet-preview");
    } else if (mode === "tablet") {
      document.body.classList.add("tablet-preview");
      document.body.classList.remove("mobile-preview");
    } else {
      document.body.classList.remove("mobile-preview");
      document.body.classList.remove("tablet-preview");
    }

    if (mode) {
      localStorage.setItem("preferredPreviewMode", mode);
    } else {
      localStorage.removeItem("preferredPreviewMode");
    }
  }, []);

  useEffect(() => {
    // Always default to null (native view) for initial load
    // Only use saved preference if explicitly requested by user
    setPreviewMode(null);

    // Ensure classes are removed on component mount
    document.body.classList.remove("mobile-preview");
    document.body.classList.remove("tablet-preview");

    // Clean up classes when component unmounts
    return () => {
      document.body.classList.remove("mobile-preview");
      document.body.classList.remove("tablet-preview");
    };
  }, []);

  // Get the actual device width class even when in preview mode
  const getNativeDeviceClass = () => {
    if (windowWidth <= BREAKPOINTS.mobile) return PREVIEW_WIDTHS.mobile;
    if (windowWidth <= BREAKPOINTS.tablet) return PREVIEW_WIDTHS.tablet;
    return PREVIEW_WIDTHS.desktop;
  };

  // Get the appropriate width class based on preview mode or native device
  const getWidthClass = () => {
    if (!previewMode) {
      return getNativeDeviceClass();
    }
    return PREVIEW_WIDTHS[previewMode] || PREVIEW_WIDTHS.desktop;
  };

  const widthClass = getWidthClass();

  return (
    <PreviewModeContext.Provider
      value={{
        previewMode,
        setPreviewMode: updatePreviewMode,
        widthClass,
        nativeDeviceClass: getNativeDeviceClass(),
      }}
    >
      {children}
    </PreviewModeContext.Provider>
  );
}

export const usePreviewMode = () => useContext(PreviewModeContext);
